import React, { useState } from "react";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  CircularProgress,
  TextField,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import Swal from "sweetalert2";
import * as XLSX from "xlsx";
import firebase, {
  functionBaseUrl,
  axiosWithToken,
} from "../../common/firebase";
import {
  defaultHeightToDefaultHipWidth,
  femaleHeightToDefaultHipWidth,
  maleHeightToDefaultHipWidth,
} from "./misc/hipWidthTable";
import { listOfSubscriptions } from "../../common/envConfig";

// New XLSX structure (matches "Baseball Player Profile Information.xlsx")
const REQUIRED_HEADERS = ["First Name", "Last Name", "Email"];
const OPTIONAL_HEADERS = [
  "Date Of Birth",
  "Gender",
  "Height",
  "Bats",
  "Throws",
];

// If you still want a download link, generate the template on demand (no base64 blob needed)
function downloadTemplate() {
  const headers = [
    [
      "First Name",
      "Last Name",
      "Email",
      "Date Of Birth", // recommended format: YYYY-MM-DD (or a real Excel date)
      "Gender", // Male/Female/Prefer not to answer
      "Height", // must match hipWidth table keys
      "Bats", // R/L/S
      "Throws", // R/L
    ],
  ];

  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.aoa_to_sheet(headers);
  XLSX.utils.book_append_sheet(wb, ws, "Sheet1");
  XLSX.writeFile(wb, "Baseball Player Profile Information.xlsx");
}

function validateHeaders(row0) {
  const headers = Object.keys(row0 || {});
  const missing = REQUIRED_HEADERS.filter((h) => !headers.includes(h));
  return { ok: missing.length === 0, missing, headers };
}

const cleanEmail = (v) => (v || "").toString().toLowerCase().replace(/ /g, "");

const normGender = (v) => {
  const g = (v || "").toString().trim().toUpperCase();
  if (["MALE", "M"].includes(g)) return "MALE";
  if (["FEMALE", "F"].includes(g)) return "FEMALE";
  if (
    [
      "PREFER NOT TO ANSWER",
      "PREFER_NOT_TO_ANSWER",
      "PREFER NOT",
      "NA",
      "N/A",
    ].includes(g)
  )
    return "PREFER NOT TO ANSWER";
  return "";
};

const normHeightKey = (v) => {
  if (v === undefined || v === null || v === "") return "";
  // IMPORTANT: hipWidth tables might be keyed by number or string.
  // We'll try number first, then string fallback in getHipWidth.
  const n = Number(v);
  if (!Number.isNaN(n)) return n;
  return v.toString().trim();
};

const formatDob = (dobVal) => {
  if (!dobVal) return "";
  // If XLSX parses dates into JS Date objects
  if (dobVal instanceof Date && !Number.isNaN(dobVal.getTime())) {
    const mm = String(dobVal.getMonth() + 1).padStart(2, "0");
    const dd = String(dobVal.getDate()).padStart(2, "0");
    const yyyy = String(dobVal.getFullYear());
    return `${mm}/${dd}/${yyyy}`; // keeping your backend-friendly style
  }
  // If it’s a string like YYYY-MM-DD or MM-DD-YYYY, keep it (or normalize if you want)
  return dobVal.toString().trim();
};

function ImportStudentsEnterpriseComponent({ open, close, uid, name, admin }) {
  const [isLoading, setIsLoading] = useState(false);
  const [students, setStudents] = useState([]);
  const [email, setEmail] = useState("");

  const handleCoachEmail = (event) => setEmail(event.target.value);

  function getHipWidth(gender, heightKey) {
    const g = (gender || "").toString().toUpperCase();
    // Try numeric key and string key, because tables differ by implementation
    const tryGet = (table) => {
      if (!table) return undefined;
      if (heightKey in table) return table[heightKey];
      const strKey = heightKey?.toString?.();
      if (strKey && strKey in table) return table[strKey];
      const numKey = Number(heightKey);
      if (!Number.isNaN(numKey) && numKey in table) return table[numKey];
      return undefined;
    };

    switch (g) {
      case "MALE":
        return tryGet(maleHeightToDefaultHipWidth);
      case "FEMALE":
        return tryGet(femaleHeightToDefaultHipWidth);
      case "PREFER NOT TO ANSWER":
        return tryGet(defaultHeightToDefaultHipWidth);
      default:
        return undefined;
    }
  }

  const handleFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const bstr = e.target.result;

        // cellDates:true helps parse Excel date cells as JS Date objects when possible
        const wb = XLSX.read(bstr, { type: "binary", cellDates: true });

        const wsname = wb.SheetNames[0];
        const ws = wb.Sheets[wsname];

        // defval:'' ensures missing cells become empty strings (less undefined chaos)
        const data = XLSX.utils.sheet_to_json(ws, { defval: "" });

        if (!data || data.length === 0) {
          Swal.fire({
            title:
              '<p style="font-size:70%;">No record found, please try again!</p>',
            icon: "error",
            showConfirmButton: true,
            showCancelButton: false,
            confirmButtonText: "Ok",
            customClass: { container: "my-swal" },
          });
          setStudents([]);
          return;
        }

        const { ok, missing } = validateHeaders(data[0]);
        if (!ok) {
          Swal.fire({
            title: `<p style="font-size:70%;">Please use the correct template. Missing columns: ${missing.join(", ")}</p>`,
            icon: "error",
            showConfirmButton: true,
            showCancelButton: false,
            confirmButtonText: "Ok",
            customClass: { container: "my-swal" },
          });
          setStudents([]);
          return;
        }

        setStudents(data);
      } catch (err) {
        console.error(err);
        Swal.fire({
          title:
            '<p style="font-size:70%;">Failed to read file. Please try again!</p>',
          icon: "error",
          confirmButtonText: "Ok",
          customClass: { container: "my-swal" },
        });
        setStudents([]);
      }
    };

    reader.readAsBinaryString(file);
  };

  const handleImport = async () => {
    setIsLoading(true);
    let importedCount = 0;
    let errorAccounts = [];

    // Check if coach account exists
    if (admin) {
      const coachEmail = cleanEmail(email);
      const coachDoc = await firebase
        .firestore()
        .collection("users")
        .where("email", "==", coachEmail)
        .get();

      if (coachDoc.size === 0) {
        Swal.fire({
          title: `<p style="font-size:70%;">No coach account found with email: ${coachEmail}</p>`,
          showConfirmButton: false,
          icon: "warning",
          customClass: { container: "my-swal" },
          showCloseButton: true,
          allowOutsideClick: true,
        });
        setIsLoading(false);
        return false;
      } else {
        coachDoc.forEach((doc) => {
          uid = doc.id;
          name = `${doc.data().firstName} ${doc.data().lastName}`;
        });
      }
    }

    for (const student of students) {
      try {
        const firstName = (student["First Name"] || "").toString().trim();
        const lastName = (student["Last Name"] || "").toString().trim();
        const studentEmail = cleanEmail(student["Email"]);

        if (!firstName || !lastName || !studentEmail) continue;

        const gender = normGender(student["Gender"]);
        const heightKey = normHeightKey(student["Height"]);

        // Mapping for your new structure:
        // Bats -> hitting + dominantHand (common baseball convention)
        // Throws -> pitching (keeping your existing backend field name)
        const bats = (student["Bats"] || "").toString().trim();
        const throwsArm = (student["Throws"] || "").toString().trim();

        const output = {
          email: studentEmail,
          firstName,
          lastName,

          dob: formatDob(student["Date Of Birth"]),
          gender,
          height: student["Height"] ? student["Height"] : "",

          coachId: uid,
          coachName: name,

          dominantHand: bats ? bats : "",
          hitting: bats ? bats : "",
          pitching: throwsArm ? throwsArm : "",

          hipWidth:
            heightKey && gender
              ? getHipWidth(gender, heightKey) || "7.2"
              : "7.2",

          subPlan: listOfSubscriptions.STUDENT_FREE,
          subPeriod: 0,
        };

        await axiosWithToken(functionBaseUrl + "/api/v3/createStudent", {
          method: "post",
          data: output,
        })
          .then((response) => {
            if (response.status === 200) importedCount++;
          })
          .catch((err) => {
            errorAccounts.push(output.email);
            console.log(err);
          });
      } catch (e) {
        errorAccounts.push(cleanEmail(student?.["Email"]));
        console.error(e);
      }
    }

    setIsLoading(false);
    close();

    if (importedCount === 0) {
      await Swal.fire({
        title: `<p style="font-size:70%;">No records imported!</p>`,
        icon: "error",
        confirmButtonText: "Ok",
        allowOutsideClick: false,
      });
    } else if (importedCount < students.length) {
      await Swal.fire({
        title: `<p style="font-size:70%;">${importedCount} accounts created successfully!</p>
          <p style="font-size:50%;">${students.length - importedCount} accounts already exist (or failed)</p>`,
        icon: "warning",
        confirmButtonText: "Ok",
        allowOutsideClick: false,
      }).then(() => {
        window.location.reload();
      });
    } else {
      await Swal.fire({
        title: `<p style="font-size:70%;">${importedCount} accounts created successfully!</p>`,
        icon: "success",
        confirmButtonText: "Ok",
        allowOutsideClick: false,
      }).then(() => {
        window.location.reload();
      });
    }

    console.log("Failed accounts:", errorAccounts);
  };

  return (
    <>
      <Dialog
        fullWidth
        open={open}
        onClose={close}
        aria-labelledby="form-dialog-title"
      >
        <DialogTitle id={"form-dialog-title"}>
          <div>
            Import Student Accounts
            <CloseIcon
              style={{
                float: "right",
                marginTop: "-10px",
                marginRight: "-15px",
              }}
              onClick={close}
            />
          </div>
        </DialogTitle>

        <DialogContent>
          <Grid container spacing={1}>
            {admin && (
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  required
                  margin="dense"
                  label="Coach Email"
                  onChange={handleCoachEmail}
                  defaultValue={email}
                  variant="outlined"
                  inputProps={{ maxLength: 50 }}
                />
              </Grid>
            )}

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <div style={{ fontSize: "135%" }}>
                  Select .xlsx file with columns:{" "}
                  <strong>
                    {[...REQUIRED_HEADERS, ...OPTIONAL_HEADERS].join(", ")}
                  </strong>
                </div>

                <div style={{ marginTop: 10 }}>
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={downloadTemplate}
                  >
                    Download template
                  </Button>
                </div>

                <br />

                <input
                  type="file"
                  id="fileElem"
                  accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  onChange={handleFile}
                />
              </Grid>

              <Grid item xs={12} style={{ textAlign: "center" }}>
                <strong>
                  {students?.length > 0 && (
                    <div>{students.length} records found</div>
                  )}
                </strong>
              </Grid>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          {isLoading && <CircularProgress />}

          <Button onClick={close} color="primary" disabled={isLoading}>
            Cancel
          </Button>

          <Button
            variant="contained"
            color="primary"
            onClick={handleImport}
            disabled={students.length === 0 || isLoading}
          >
            Import
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export const ImportStudentsEnterprise = ImportStudentsEnterpriseComponent;
